import fetch from 'isomorphic-fetch';
import { storage } from '../features/common/platform/PlatformTools';
import ACTION_TYPE from './ACTION_TYPE';

interface Options extends RequestInit {
    headers?: {
        'x-csrf-token'?: string;
        'Content-Type'?: string;
    };
}
interface Body {
    csrfToken: string;
    errorMessage?: string;
    details?: string;
}
interface Response {
    status: number;
    json: () => Promise<Body>;
}
export interface ResponseError {
    name: string;
    message: string;
    statusCode?: number;
    details?: string;
}

const csrfPromise = fetch('/appstore/api/csrf', {
    credentials: 'same-origin',
})
    .catch(() => {
        throw Error('Could not contact the server, try again in a few minutes');
    })
    .then(handleJsonResponse)
    .then((result) => result?.csrfToken);

export function fetchJson(url: string, options: Options = {}) {
    // This needs to be conditional in case we want to make an explicitly unauthenticated request etc.
    options.credentials || (options.credentials = 'same-origin');
    options.headers = options.headers || {};

    return csrfPromise
        .then((csrf) => {
            if (options.headers) {
                options.headers['x-csrf-token'] = csrf || '';
            }

            return fetchWithBetterError(url, options);
        })
        .then(handleJsonResponse);
}

export function fetchWithoutBody(url: string, options: Options = {}) {
    // This needs to be conditional in case we want to make an explicitly unauthenticated request etc.
    options.credentials || (options.credentials = 'same-origin');
    options.headers = options.headers || {};

    return csrfPromise
        .then((csrf) => {
            if (options.headers) {
                options.headers['x-csrf-token'] = csrf || '';
            }

            return fetchWithBetterError(url, options);
        })
        .then((response) => {
            if (response.status >= 200 && response.status <= 299) {
                return;
            }

            return response.json().then((body) => {
                const error: ResponseError = Error(body.errorMessage);

                error.statusCode = response.status;

                throw error;
            });
        });
}

export { ACTION_TYPE };

function handleJsonResponse(response: Response): Promise<any> {
    if (response.status === 204) {
        return Promise.resolve();
    }

    return response.json().then((body) => {
        if (response.status >= 400) {
            const error: ResponseError = Error(body.errorMessage);
            error.statusCode = response.status;
            if (body.details) {
                error.details = body.details;
            }
            throw error;
        }

        return body;
    });
}

function fetchWithBetterError(url: string, options: Options) {
    const urlObj = new URL(url.startsWith('https://') ? url : `${window.location.origin}/appstore/${url}`);
    const { platformId } = storage;
    urlObj.searchParams.append('platformId', platformId);

    return fetch(urlObj.href, options).catch(() => {
        // For our app this only occurs when the server is unreachable
        throw Error('Could not contact the server, try again in a few minutes');
    });
}
